import React, { useState, useEffect, useCallback } from 'react';
import { KitchenStaffLayout } from '../../components/staff/KitchenStaffLayout';
import { ClipboardList, Clock, ChevronRight, RefreshCw, Ban } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUSES = [
  { key: 'Order Placed', color: 'bg-blue-500', lightBg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  { key: 'Order Accepted', color: 'bg-indigo-500', lightBg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' },
  { key: 'In Preparation', color: 'bg-amber-500', lightBg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  { key: 'Ready to Pick', color: 'bg-purple-500', lightBg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  { key: 'Picked Up', color: 'bg-cyan-500', lightBg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
  { key: 'Delivered', color: 'bg-green-500', lightBg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  { key: 'Cancelled by Customer', color: 'bg-red-500', lightBg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
  { key: 'Cancelled By Staff', color: 'bg-red-500', lightBg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
];

const NEXT_STATUS = {
  'Order Placed': 'Order Accepted',
  'Order Accepted': 'In Preparation',
  'In Preparation': 'Ready to Pick',
  'Ready to Pick': 'Picked Up',
  'Picked Up': 'Delivered',
};

// Role -> which statuses they see (null = all statuses)
const ROLE_STATUSES = {
  'Bar Expeditor': null,
  'Kitchen Expeditor': null,
  'Supervisor': null,
  'Bartender': ['Order Accepted', 'In Preparation'],
  'Chef': ['Order Accepted', 'In Preparation'],
  'Drinks Runner': ['Ready to Pick', 'Picked Up'],
  'Food Runner': ['Ready to Pick', 'Picked Up'],
  'Pool And Beach Server': ['Order Placed', 'Order Accepted', 'In Preparation', 'Ready to Pick', 'Picked Up', 'Delivered'],
  'Food and Beverages Server': ['Order Placed', 'Order Accepted', 'In Preparation', 'Ready to Pick', 'Picked Up', 'Delivered'],
};

// Role -> which order types they see (null = all)
const ROLE_ORDER_TYPE = {
  'Bar Expeditor': 'Bar',
  'Bartender': 'Bar',
  'Drinks Runner': 'Bar',
  'Kitchen Expeditor': 'Kitchen',
  'Chef': 'Kitchen',
  'Food Runner': 'Kitchen',
  'Supervisor': null, // all
  'Pool And Beach Server': null,
  'Food and Beverages Server': null,
};

// Roles that are read-only (no status change or cancel actions)
const READ_ONLY_ROLES = ['Pool And Beach Server', 'Pool and Beach Server', 'Food and Beverages Server', 'Food And Beverages Server'];

const CANCELLATION_REASONS = [
  'Cancelled by Customer',
  'Overing',
  'Void',
  'Code 86',
  'Cancelled',
  'Comp',
  'Gesture',
];

// Map each cancellation reason to the kanban column the order should move to
const REASON_TO_COLUMN = {
  'Cancelled by Customer': 'Cancelled by Customer',
  'Overing': 'Cancelled by Customer',
  'Void': 'Cancelled by Customer',
  'Code 86': 'Cancelled By Staff',
  'Cancelled': 'Cancelled By Staff',
  'Comp': 'Cancelled By Staff',
  'Gesture': 'Cancelled By Staff',
};

// Statuses past Order Accepted — customer-initiated cancellation is not allowed
const CUSTOMER_BLOCKED_STATUSES = new Set([
  'In Preparation', 'Ready to Pick', 'Picked Up', 'Delivered',
]);

// Reasons that require the "Was the item made?" follow-up at In Preparation
const MADE_FLAG_REASONS = new Set(['Cancelled by Staff', 'Overing']);

export const StaffOrders = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('Both');
  const [roleName, setRoleName] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDescription, setCancelDescription] = useState('');
  const [selectedItemIndices, setSelectedItemIndices] = useState([]);
  const [itemWasMade, setItemWasMade] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsed = JSON.parse(userData);
      const name = parsed.roleName || '';
      setRoleName(name);
      // Auto-set type filter based on role
      const roleType = ROLE_ORDER_TYPE[name];
      if (roleType) {
        setTypeFilter(roleType);
      }
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;
      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      const response = await axios.get(`${BACKEND_URL}/api/orders/property/${propertyId}`);
      if (response.data.code === 200) {
        setOrders(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const moveOrder = async (orderId, newStatus, extra = {}) => {
    setUpdatingId(orderId);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/orders/${orderId}/status`, { status: newStatus, ...extra });
      if (response.data.code === 200) {
        if (extra.cancelItemIndices) {
          await fetchOrders();
          toast({
            title: response.data.data?.allCancelled ? 'Order Cancelled' : 'Items Cancelled',
            description: response.data.data?.allCancelled
              ? `Order moved to "${newStatus}"`
              : `${response.data.data?.cancelledItemCount} item(s) cancelled. Order remains in current column.`,
          });
        } else {
          setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
          toast({ title: 'Status Updated', description: `Order moved to "${newStatus}"` });
        }
      } else {
        toast({ title: 'Error', description: response.data.message, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update order status', variant: 'destructive' });
    } finally {
      setUpdatingId(null);
    }
  };

  const openCancelModal = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    const initiallySelected = (order?.items || [])
      .map((it, idx) => (!it.cancelled ? idx : null))
      .filter(idx => idx !== null);
    setCancellingOrderId(orderId);
    setCancelReason('');
    setCancelDescription('');
    setItemWasMade(null);
    setSelectedItemIndices(initiallySelected);
    setCancelModalOpen(true);
  };

  const toggleItemIndex = (idx) => {
    setSelectedItemIndices(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const selectAllItems = () => {
    const order = orders.find(o => o.id === cancellingOrderId);
    if (!order) return;
    const all = (order.items || [])
      .map((it, idx) => (!it.cancelled ? idx : null))
      .filter(idx => idx !== null);
    setSelectedItemIndices(all);
  };

  const deselectAllItems = () => setSelectedItemIndices([]);

  const confirmCancel = async () => {
    if (!cancelReason) {
      toast({ title: 'Required', description: 'Please select a cancellation reason', variant: 'destructive' });
      return;
    }
    if (!cancelDescription.trim()) {
      toast({ title: 'Required', description: 'Please provide a description', variant: 'destructive' });
      return;
    }
    if (selectedItemIndices.length === 0) {
      toast({ title: 'Required', description: 'Please select at least one item to cancel', variant: 'destructive' });
      return;
    }
    const order = orders.find(o => o.id === cancellingOrderId);
    const requiresMadeFlag = order && order.status === 'In Preparation' && MADE_FLAG_REASONS.has(cancelReason);
    if (requiresMadeFlag && itemWasMade === null) {
      toast({ title: 'Required', description: 'Please indicate whether the item was already made', variant: 'destructive' });
      return;
    }
    await moveOrder(cancellingOrderId, REASON_TO_COLUMN[cancelReason] || 'Cancelled By Staff', {
      cancelItemIndices: selectedItemIndices,
      cancellationReason: cancelReason,
      cancellationDescription: cancelDescription.trim(),
      ...(requiresMadeFlag ? { itemWasMade: itemWasMade } : {}),
    });
    setCancelModalOpen(false);
    setCancellingOrderId(null);
    setSelectedItemIndices([]);
    setItemWasMade(null);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getOrdersByStatus = (status) => {
    return orders.filter(o => {
      if (o.status !== status) return false;
      if (typeFilter === 'Kitchen') return o.orderType === 'Kitchen';
      if (typeFilter === 'Bar') return o.orderType === 'Bar';
      return true;
    });
  };

  const filteredOrders = typeFilter === 'Both' ? orders
    : orders.filter(o => o.orderType === typeFilter);

  const visibleStatuses = ROLE_STATUSES[roleName] === null || !ROLE_STATUSES[roleName]
    ? STATUSES
    : STATUSES.filter(s => ROLE_STATUSES[roleName].includes(s.key));

  if (loading) {
    return (
      <KitchenStaffLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </KitchenStaffLayout>
    );
  }

  return (
    <KitchenStaffLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Orders</h1>
            <p className="text-slate-500 text-sm mt-1">{filteredOrders.length} orders{typeFilter !== 'Both' ? ` (${typeFilter})` : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Only show type filter toggle for roles with all-order access (Supervisor) */}
            {ROLE_ORDER_TYPE[roleName] === null || ROLE_ORDER_TYPE[roleName] === undefined ? (
            <div className="flex bg-slate-100 rounded-lg p-1" data-testid="staff-order-type-filter">
              {['Both', 'Kitchen', 'Bar'].map((type) => (
                <button
                  key={type}
                  onClick={() => setTypeFilter(type)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    typeFilter === type
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            ) : (
              <span className="px-3 py-1.5 rounded-md text-sm font-medium bg-slate-100 text-slate-700" data-testid="staff-order-type-label">
                {ROLE_ORDER_TYPE[roleName]} Orders
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchOrders(); }} data-testid="refresh-staff-orders-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="staff-orders-kanban">
          {visibleStatuses.map((col) => {
            const columnOrders = getOrdersByStatus(col.key);
            return (
              <div key={col.key} className="min-w-[280px] w-[280px] flex-shrink-0 flex flex-col">
                {/* Column Header */}
                <div className={`${col.color} rounded-t-xl px-4 py-3 flex items-center justify-between`}>
                  <span className="text-white font-semibold text-sm truncate">{col.key}</span>
                  <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full">{columnOrders.length}</span>
                </div>

                {/* Column Body */}
                <div className={`${col.lightBg} ${col.border} border border-t-0 rounded-b-xl p-3 flex-1 space-y-3 min-h-[200px]`}>
                  {columnOrders.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center pt-8">No orders</p>
                  ) : (
                    columnOrders.map((order) => (
                      <div key={order.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 space-y-2" data-testid={`staff-order-card-${order.id}`}>
                        {/* Order ID + Type */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono font-bold text-slate-500">#{order.id}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${order.orderType === 'Bar' ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'}`}>
                            {order.orderType || 'Kitchen'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-slate-800 truncate">{order.guestName || 'Guest'}</span>
                          <span className="text-xs text-slate-400">Rm {order.roomNumber || '—'}</span>
                        </div>

                        <div className="space-y-0.5">
                          {order.items?.map((item, idx) => (
                            <div key={idx}>
                              <div className={`flex justify-between text-xs ${item.cancelled ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
                                <span className="truncate mr-2">{item.menuItemName} x{item.quantity}</span>
                                <span className="shrink-0">${(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                              {item.cancelled && item.cancellationReason && (
                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-red-100 text-red-700">
                                    {item.cancellationReason}
                                  </span>
                                  {item.costInvolved === true && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-rose-100 text-rose-700" title="Internal: cost was involved">
                                      Cost
                                    </span>
                                  )}
                                  {item.costInvolved === false && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-slate-100 text-slate-500" title="Internal: no cost involved">
                                      No Cost
                                    </span>
                                  )}
                                </div>
                              )}
                              {item.notes && !item.cancelled && (
                                <p className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded mt-0.5 italic" data-testid={`order-item-remark-${order.id}-${idx}`}>
                                  {item.notes}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />
                            <span>{formatTime(order.createdAt)}</span>
                          </div>
                          <span className="text-sm font-semibold text-slate-800">${order.totalAmount?.toFixed(2)}</span>
                        </div>

                        {NEXT_STATUS[order.status] && !READ_ONLY_ROLES.some(r => r.toLowerCase() === roleName.toLowerCase()) && (
                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              className="flex-1 h-7 text-xs"
                              disabled={updatingId === order.id}
                              onClick={() => moveOrder(order.id, NEXT_STATUS[order.status])}
                            >
                              {NEXT_STATUS[order.status]}
                              <ChevronRight className="w-3 h-3 ml-1" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                              disabled={updatingId === order.id}
                              onClick={() => openCancelModal(order.id)}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancellation Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="staff-cancel-order-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" />
              Cancel Items
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Items checklist */}
            {(() => {
              const order = orders.find(o => o.id === cancellingOrderId);
              const items = order?.items || [];
              if (items.length === 0) return null;
              const cancellableCount = items.filter(it => !it.cancelled).length;
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Select items to cancel *
                    </label>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={selectAllItems}
                        className="text-xs text-slate-500 hover:text-slate-700 underline"
                        data-testid="staff-select-all-cancel-items"
                      >
                        All
                      </button>
                      <span className="text-xs text-slate-300">·</span>
                      <button
                        type="button"
                        onClick={deselectAllItems}
                        className="text-xs text-slate-500 hover:text-slate-700 underline"
                        data-testid="staff-deselect-all-cancel-items"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
                    {items.map((it, idx) => {
                      const alreadyCancelled = it.cancelled;
                      const checked = selectedItemIndices.includes(idx);
                      return (
                        <label
                          key={idx}
                          className={`flex items-center gap-3 p-2.5 cursor-pointer transition-colors ${
                            alreadyCancelled
                              ? 'bg-slate-50 cursor-not-allowed opacity-60'
                              : checked
                                ? 'bg-red-50'
                                : 'hover:bg-slate-50'
                          }`}
                          data-testid={`staff-cancel-item-${idx}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={alreadyCancelled}
                            onChange={() => toggleItemIndex(idx)}
                            className="w-4 h-4 rounded text-red-600 focus:ring-red-400 disabled:opacity-50"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`text-sm font-medium ${alreadyCancelled ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                                {it.menuItemName} <span className="text-slate-400 font-normal">x{it.quantity}</span>
                              </p>
                              <span className="text-xs text-slate-500 shrink-0">${(it.price * it.quantity).toFixed(2)}</span>
                            </div>
                            {alreadyCancelled && it.cancellationReason && (
                              <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 mt-0.5">
                                Already cancelled · {it.cancellationReason}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {cancellableCount > 0 && (
                    <p className="text-xs text-slate-500 mt-1.5">
                      {selectedItemIndices.length} of {cancellableCount} item(s) selected
                      {selectedItemIndices.length === cancellableCount && cancellableCount > 0
                        ? ' — cancelling all remaining items will move the order to the cancelled column.'
                        : ''}
                    </p>
                  )}
                </div>
              );
            })()}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason *</label>
              <select
                value={cancelReason}
                onChange={(e) => { setCancelReason(e.target.value); setItemWasMade(null); }}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                data-testid="staff-cancel-reason-select"
              >
                <option value="">Select reason...</option>
                {(() => {
                  const order = orders.find(o => o.id === cancellingOrderId);
                  const customerBlocked = order && CUSTOMER_BLOCKED_STATUSES.has(order.status);
                  return CANCELLATION_REASONS.filter(r => !(customerBlocked && r === 'Cancelled by Customer')).map(r => (
                    <option key={r} value={r}>{r}</option>
                  ));
                })()}
              </select>
            </div>

            {/* "Was the item already made?" — only at In Preparation + Staff/Overing */}
            {(() => {
              const order = orders.find(o => o.id === cancellingOrderId);
              if (!order || order.status !== 'In Preparation' || !MADE_FLAG_REASONS.has(cancelReason)) return null;
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="staff-item-was-made-block">
                  <p className="text-sm font-medium text-amber-900 mb-2">
                    Was the item already made? <span className="text-amber-700 font-normal">(internal — drives cost tracking)</span>
                  </p>
                  <div className="flex gap-3">
                    <label className={`flex-1 cursor-pointer rounded-md border-2 px-3 py-2 text-sm font-medium text-center transition-colors ${
                      itemWasMade === true ? 'border-amber-500 bg-amber-100 text-amber-900' : 'border-amber-200 bg-white text-amber-700 hover:border-amber-300'
                    }`} data-testid="staff-item-was-made-yes">
                      <input
                        type="radio"
                        name="staffItemWasMade"
                        className="sr-only"
                        checked={itemWasMade === true}
                        onChange={() => setItemWasMade(true)}
                      />
                      Yes, it was made
                    </label>
                    <label className={`flex-1 cursor-pointer rounded-md border-2 px-3 py-2 text-sm font-medium text-center transition-colors ${
                      itemWasMade === false ? 'border-emerald-500 bg-emerald-100 text-emerald-900' : 'border-amber-200 bg-white text-amber-700 hover:border-amber-300'
                    }`} data-testid="staff-item-was-made-no">
                      <input
                        type="radio"
                        name="staffItemWasMade"
                        className="sr-only"
                        checked={itemWasMade === false}
                        onChange={() => setItemWasMade(false)}
                      />
                      No, not yet
                    </label>
                  </div>
                </div>
              );
            })()}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Description *</label>
              <textarea
                value={cancelDescription}
                onChange={(e) => setCancelDescription(e.target.value)}
                placeholder="Provide reason for cancellation..."
                rows={3}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                data-testid="staff-cancel-description-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Go Back
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmCancel}
              disabled={updatingId === cancellingOrderId}
              data-testid="staff-confirm-cancel-btn"
            >
              <Ban className="w-4 h-4 mr-1.5" />
              Confirm Cancel ({selectedItemIndices.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </KitchenStaffLayout>
  );
};

export default StaffOrders;
